import {
  Box,
  Button,
  Checkbox,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTablePagination } from '@/components/Shared/DataTable';
import type { ProductActionId, RegistryProduct } from '@/types/productRegistry';
import { ProductCardRow, ProductTableRow } from './ProductTableRow';

type SortableColumn =
  | 'name'
  | 'category'
  | 'status'
  | 'environment'
  | 'activeUsers'
  | 'lastModified';

interface ProductTableProps {
  products: RegistryProduct[];
  total: number;
  page: number;
  pageSize: number;
  selectedIds: string[];
  sortBy: SortableColumn;
  sortDirection: 'asc' | 'desc';
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (column: SortableColumn) => void;
  onSelectRow: (productId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onAction: (actionId: ProductActionId, product: RegistryProduct) => void;
}

const columns: Array<{
  id: SortableColumn;
  labelKey: string;
  align?: 'right';
}> = [
  { id: 'name', labelKey: 'productRegistry.productRegistryColumnProductName' },
  { id: 'category', labelKey: 'productRegistry.productRegistryColumnCategory' },
  { id: 'status', labelKey: 'productRegistry.productRegistryColumnStatus' },
  {
    id: 'environment',
    labelKey: 'productRegistry.productRegistryColumnEnvironment',
  },
  {
    id: 'activeUsers',
    labelKey: 'productRegistry.productRegistryColumnActiveUsers',
    align: 'right',
  },
  {
    id: 'lastModified',
    labelKey: 'productRegistry.productRegistryColumnLastModified',
  },
];

export function ProductTableSkeleton() {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} height={48} sx={{ mb: 1 }} />
      ))}
    </Paper>
  );
}

export const ProductTable = memo(function ProductTable({
  products,
  total,
  page,
  pageSize,
  selectedIds,
  sortBy,
  sortDirection,
  isLoading = false,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onSelectRow,
  onSelectAll,
  onAction,
}: ProductTableProps) {
  const { t } = useTranslation('entity-portal');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allSelected =
    products.length > 0 &&
    products.every((product) => selectedSet.has(product.id));
  const someSelected = products.some((product) => selectedSet.has(product.id));

  if (isLoading) {
    return <ProductTableSkeleton />;
  }

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {isMobile ? (
        <Stack spacing={1.5} sx={{ p: 2 }}>
          {products.map((product) => (
            <ProductCardRow
              key={product.id}
              product={product}
              selected={selectedSet.has(product.id)}
              onSelect={onSelectRow}
              onAction={onAction}
            />
          ))}
        </Stack>
      ) : (
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table
            stickyHeader
            size="small"
            aria-label={t('productRegistry.productRegistryTableLabel')}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <Checkbox
                    indeterminate={someSelected && !allSelected}
                    checked={allSelected}
                    onChange={(event) => onSelectAll(event.target.checked)}
                    inputProps={{
                      'aria-label': t(
                        'productRegistry.productRegistrySelectAll',
                      ),
                    }}
                  />
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    <TableSortLabel
                      active={sortBy === column.id}
                      direction={sortBy === column.id ? sortDirection : 'asc'}
                      onClick={() => onSortChange(column.id)}
                    >
                      {t(column.labelKey)}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ bgcolor: 'background.paper' }}>
                  {t('productRegistry.productRegistryColumnActions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <ProductTableRow
                  key={product.id}
                  product={product}
                  selected={selectedSet.has(product.id)}
                  onSelect={onSelectRow}
                  onAction={onAction}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DataTablePagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </Paper>
  );
});

interface ProductEmptyStateProps {
  onClearFilters: () => void;
  onAddProduct: () => void;
}

export function ProductEmptyState({
  onClearFilters,
  onAddProduct,
}: ProductEmptyStateProps) {
  const { t } = useTranslation('entity-portal');

  return (
    <Box
      sx={{
        py: 6,
        px: 3,
        textAlign: 'center',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('productRegistry.productRegistryEmptyTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('productRegistry.productRegistryEmptyDescription')}
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="center"
      >
        <Button variant="outlined" onClick={onClearFilters}>
          {t('productRegistry.productRegistryClearFilters')}
        </Button>
        <Button variant="contained" onClick={onAddProduct}>
          {t('productRegistry.productRegistryAddProduct')}
        </Button>
      </Stack>
    </Box>
  );
}
